package com.unrealmi.site.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobwebx.markdown.markdown
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.unrealmi.site.gradientText
import com.unrealmi.site.toSitePalette

val MarkdownStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(48.cssRem)
            .padding(leftRight = 1.5.cssRem, top = 2.cssRem, bottom = 4.cssRem)
    }
    Breakpoint.MD { Modifier.padding(top = 4.cssRem, bottom = 6.cssRem) }

    cssRule("h1") {
        Modifier
            .fontSize(2.8.cssRem)
            .fontWeight(800)
            .letterSpacing((-1).px)
            .margin(bottom = 1.5.cssRem)
            .lineHeight(1.15)
            .then(Modifier.gradientText())
    }

    cssRule("h2") {
        Modifier
            .fontSize(1.9.cssRem)
            .fontWeight(700)
            .letterSpacing((-0.5).px)
            .margin(top = 3.cssRem, bottom = 1.cssRem)
    }

    cssRule("h3") {
        Modifier
            .fontSize(1.35.cssRem)
            .fontWeight(700)
            .margin(top = 2.cssRem, bottom = 0.75.cssRem)
    }

    cssRule("h4") {
        Modifier
            .fontSize(1.1.cssRem)
            .fontWeight(FontWeight.Bolder)
            .margin(top = 1.cssRem, bottom = 0.5.cssRem)
    }

    cssRule("p") {
        Modifier
            .color(colorMode.toSitePalette().subtle)
            .lineHeight(1.8)
            .margin(bottom = 1.cssRem)
    }

    cssRule("strong") {
        Modifier.color(colorMode.toPalette().color).fontWeight(FontWeight.Bold)
    }

    cssRule("a") {
        Modifier.color(colorMode.toSitePalette().brand.primary)
    }

    cssRule("hr") {
        Modifier
            .fillMaxWidth()
            .height(1.px)
            .border(0.px)
            .backgroundColor(colorMode.toSitePalette().border)
            .margin(topBottom = 2.5.cssRem)
    }

    cssRule("ul") {
        Modifier.fillMaxWidth().overflowWrap(OverflowWrap.BreakWord)
    }

    cssRule(" :is(li,ol,ul)") {
        Modifier.margin(bottom = 0.4.cssRem).lineHeight(1.7)
    }

    cssRule("li::marker") {
        Modifier.color(colorMode.toSitePalette().brand.primary)
    }

    cssRule("code") {
        Modifier
            .color(colorMode.toSitePalette().brand.primary)
            .fontWeight(FontWeight.Bolder)
    }

    cssRule("pre") {
        Modifier
            .margin(top = 0.5.cssRem, bottom = 2.cssRem)
            .fillMaxWidth()
    }
    cssRule("pre > code") {
        Modifier
            .display(DisplayStyle.Block)
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().nearBackground)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(0.5.cssRem)
            .padding(1.cssRem)
            .fontSize(0.9.cssRem)
            .color(colorMode.toPalette().color)
            .overflow { x(Overflow.Auto) }
    }
}

@InitRoute
fun initMarkdownLayout(ctx: InitRouteContext) {
    val title = ctx.markdown!!.frontMatter["title"]?.singleOrNull()
    require(title != null) { "Markdown file must set \"title\" in frontmatter" }

    ctx.data.add(PageLayoutData(title))
}

@Composable
@Layout(".components.layouts.PageLayout")
fun MarkdownLayout(content: @Composable () -> Unit) {
    Div(MarkdownStyle.toAttrs()) {
        content()
    }
}
